/**
 * Compose helpers (spec §11): address parsing/validation, the attachment-mention check, cid
 * generation for pasted images, the html wrapper sent to `/api/send`, the autosave fingerprint
 * and the FormData builder. All pure; the editor DOM stays in the component.
 */
import { sanitizeCompose } from './sanitize';

/** Only strings matching this are accepted as recipients. */
export const ADDRESS_RE = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/;
/** Words that suggest an attachment was meant (Arabic + English). */
export const ATTACH_MENTION_RE =
  /(مرفق|مُرفق|مرافق|طيّه|طيه|تجدون أدناه|أرفقت|ارفقت|نرفق|مرفقًا|attach(ed|ment|ing)?|enclosed|herewith)/i;
/** Cumulative attachment cap (25 MB) and the inline-image threshold (1.5 MB). */
export const MAX_ATTACH_BYTES = 25 * 1024 * 1024;
export const INLINE_IMAGE_MAX_BYTES = 1.5 * 1024 * 1024;
export const ATTACH_LIMIT_ERROR = '≤ 25MB';
/** Recipient count that shows the note, and the count that asks for confirmation. */
export const RCPT_NOTE_AT = 5;
export const RCPT_CONFIRM_AT = 15;
/** Autosave debounce. */
export const AUTOSAVE_MS = 4000;

/** Split a draft address string on `,` / `;`, trimming and dropping empties. */
export function splitAddresses(s: string | null | undefined): string[] {
  return (s ?? '').split(/[,;]/).map((x) => x.trim()).filter(Boolean);
}

export function isValidAddress(s: string): boolean {
  return ADDRESS_RE.test(s);
}

/**
 * Commit typed text as a chip: strips trailing separators, validates, ignores duplicates.
 * Returns the new list (same reference when nothing changed).
 */
export function commitAddress(list: readonly string[], typed: string): string[] {
  const v = (typed ?? '').trim().replace(/[,;]+$/, '').trim();
  if (!v || !isValidAddress(v) || list.includes(v)) return [...list];
  return [...list, v];
}

/** Whether the typed value ends with a separator (commit trigger). */
export function endsWithSeparator(typed: string): boolean {
  return /[,;]$/.test(typed);
}

/**
 * Body text minus quotes / signature / anything after a "wrote:" line, for the mention check.
 * Falls back to the raw html when no DOMParser exists (SSR/tests without DOM).
 */
export function stripQuotedForMention(html: string): string {
  if (typeof DOMParser === 'undefined') return html;
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  for (const q of body.querySelectorAll('blockquote, [class*="gmail_quote"], .tik-sig, [data-tik-quote]')) q.remove();
  const text = body.textContent || '';
  // The legacy used `\b` here, which is ASCII-only and therefore never matched after an Arabic
  // "كتب"; a script-neutral "not followed by a letter/digit" lookahead does what the spec meant.
  const cut = text.search(/^\s*(في .*كتب|On .*wrote)(?![\p{L}\p{N}])/mu);
  return cut >= 0 ? text.slice(0, cut) : text;
}

/** True when the body mentions an attachment but none is attached. */
export function mentionsAttachment(html: string, text: string, attachmentCount: number): boolean {
  if (attachmentCount > 0) return false;
  const body = stripQuotedForMention(html || '') || text || '';
  return ATTACH_MENTION_RE.test(body);
}

let cidCounter = 0;

/** `tik<base36 time><n>@mkktp` — unique enough per session for inline images. */
export function newCid(now: number = Date.now()): string {
  return `tik${now.toString(36)}${cidCounter++}@mkktp`;
}

/** Reset the cid counter (tests). */
export function resetCidCounter(): void {
  cidCounter = 0;
}

/** Ensure a pasted blob has a filename (`image-<n>.png`). */
export function fileForPaste(f: File, n: number): File {
  return f.name ? f : new File([f], `image-${n}.png`, { type: f.type });
}

/** Sum of sizes; used against `MAX_ATTACH_BYTES`. */
export function totalSize(files: readonly { size: number }[]): number {
  return files.reduce((s, f) => s + f.size, 0);
}

export function exceedsAttachCap(files: readonly { size: number }[], extra = 0): boolean {
  return totalSize(files) + extra > MAX_ATTACH_BYTES;
}

/** `<img data-tik-cid="X">` placeholders become `<img src="cid:X" style="max-width:100%">`. */
export function rewriteCidImages(html: string): string {
  return html.replace(/<img[^>]*?data-tik-cid="([^"]+)"[^>]*>/gi, (_m, cid: string) => `<img src="cid:${cid}" style="max-width:100%">`);
}

/** Sanitised, cid-rewritten, wrapped html for `/api/send`; null when the editor is empty. */
export function htmlForSend(editorHtml: string): string | null {
  if (!editorHtml || editorHtml === '<br>') return null;
  const clean = rewriteCidImages(sanitizeCompose(editorHtml));
  return '<div dir="auto" style="font-family:sans-serif;line-height:1.75">' + clean + '</div>';
}

export interface DraftFields {
  to: readonly string[];
  cc: readonly string[];
  bcc: readonly string[];
  subject: string;
  html: string;
}

/** `to|cc|bcc|subject|html` — autosave runs only when this changes. */
export function autosaveFingerprint(d: DraftFields): string {
  return d.to.join(', ') + '|' + d.cc.join(', ') + '|' + d.bcc.join(', ') + '|' + d.subject + '|' + d.html;
}

/** Skip autosave when nothing meaningful exists: no recipients, no subject, < 2 chars of text. */
export function isDraftWorthSaving(d: { to: readonly string[]; subject: string; text: string }): boolean {
  return d.to.length > 0 || !!d.subject || d.text.trim().length >= 2;
}

/** `beforeunload` guard: recipients, a subject, or more than one character of text. */
export function hasUnsavedContent(d: { to: readonly string[]; subject: string; text: string }): boolean {
  return d.to.length > 0 || !!d.subject.trim() || d.text.trim().length > 1;
}

export function recipientCount(d: { to: readonly string[]; cc: readonly string[]; bcc: readonly string[] }): number {
  return d.to.length + d.cc.length + d.bcc.length;
}

export interface SendInput {
  to: readonly string[];
  cc: readonly string[];
  bcc: readonly string[];
  subject: string;
  text: string;
  editorHtml: string;
  inReplyTo?: string;
  references?: readonly string[];
  fromName?: string;
  files: readonly File[];
  cidMap: readonly { name: string; cid: string }[];
  fwdFrom?: { folder: string; uid: number; parts: readonly unknown[] };
  sendAt?: number;
  undoSec: number;
}

/** FormData for `POST /api/send` (field set per frontend-api-usage.md §4). */
export function buildSendForm(input: SendInput): FormData {
  const fd = new FormData();
  fd.set('to', input.to.join(', '));
  if (input.cc.length) fd.set('cc', input.cc.join(', '));
  if (input.bcc.length) fd.set('bcc', input.bcc.join(', '));
  fd.set('subject', input.subject.trim());
  fd.set('text', input.text.trim());
  const html = htmlForSend(input.editorHtml);
  if (html) fd.set('html', html);
  if (input.inReplyTo) fd.set('inReplyTo', input.inReplyTo);
  if (input.references?.length) fd.set('references', input.references.join(' '));
  const fromName = input.fromName?.trim();
  if (fromName) fd.set('fromName', fromName);
  for (const f of input.files) fd.append('files', f, f.name);
  if (input.cidMap.length) fd.append('cidMap', JSON.stringify(input.cidMap));
  if (input.fwdFrom && input.fwdFrom.parts.length) {
    fd.append('fwdFrom', JSON.stringify({ folder: input.fwdFrom.folder, uid: input.fwdFrom.uid, parts: input.fwdFrom.parts }));
  }
  if (input.sendAt) fd.append('sendAt', String(input.sendAt));
  else if (input.undoSec > 0) fd.append('undoSec', String(input.undoSec));
  return fd;
}

/** Template insertion: escape `&` and `<`, newlines → `<br>` (legacy `insertHTML` payload). */
export function templateToHtml(body: string): string {
  return body.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>');
}

/** `https://` is prefixed to a typed link when no scheme is present. */
export function normalizeLink(url: string): string {
  const u = url.trim();
  return /^https?:\/\//i.test(u) ? u : 'https://' + u;
}

/** Identity option label: `"<name>, <user>"` or `<user>`. */
export function identityLabel(name: string | undefined, user: string): string {
  const n = (name || '').trim();
  return n ? `${n}, ${user}` : user;
}
